import React, {
	useEffect,
	useCallback,
	useRef,
	useContext,
	Suspense,
} from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { Route, Redirect, useHistory } from "react-router-dom";

import {
	PermissionContext,
	RedirectToLogout,
	SpinnerOverlay,
} from "components";
import { PageNotFound } from "containers/errors/pageNotFound";
import { AuthParams, RefreshInProgress, URLS } from "globalConstants";
import {
	checkTokenExpiration,
	getUserActionDate,
	hasPermissions,
	refreshToken,
	setTitleAndFaviconForAurora,
	setUserActionDate,
} from "helpers";
import usePendoInitialize from "hooks/usePendoInitialize";
import { uiActions } from "store";

import Header from "../header";
import SideMenu from "../sideMenu";

const FIVE_SECONDS = 5000;
const FIVE_MINUTES_IN_SECONDS = 5 * 60;
const FOUR_MINUTES_IN_SECONDS = 4 * 60;

const PrivateLayout = ({ pageName, body }) => {
	const dispatch = useDispatch();
	setTitleAndFaviconForAurora(pageName);

	const tabTimeoutFactor = sessionStorage.getItem("tabTimeoutFactor");

	const history = useHistory();
	const showModalTimerRef = useRef();

	const checkUserActivity = useCallback(() => {
		const currentUser = JSON.parse(localStorage.getItem("user"));
		const { pathname } = history.location;
		if (_.isEmpty(currentUser) && pathname !== URLS.login) {
			window.location.reload();
			return;
		}

		const tokenExpirationDate = new Date(currentUser.expires_date);
		const expirationByUserActionDate = getUserActionDate();

		if (!tokenExpirationDate || !expirationByUserActionDate) {
			return;
		}

		tokenExpirationDate.setSeconds(
			tokenExpirationDate.getSeconds() -
				FOUR_MINUTES_IN_SECONDS -
				tabTimeoutFactor,
		);
		expirationByUserActionDate.setSeconds(
			expirationByUserActionDate.getSeconds() +
				(currentUser.expires_in - FIVE_MINUTES_IN_SECONDS),
		);

		const now = new Date();

		if (now >= tokenExpirationDate && now < expirationByUserActionDate) {
			const refreshInProgress = localStorage.getItem(RefreshInProgress);

			if (!refreshInProgress || refreshInProgress === "false") {
				localStorage.setItem(RefreshInProgress, "true");

				refreshToken(currentUser?.refresh_token);
			}
			return;
		}

		if (now >= expirationByUserActionDate) {
			dispatch(uiActions.showSessionExpirationModal());
		} else {
			//Close pop up if user did action in another tab
			dispatch(uiActions.hideSessionExpirationModal());
		}
	}, [dispatch, history.location, tabTimeoutFactor]);

	useEffect(() => {
		handleUserAction();
		clearInterval(showModalTimerRef.current);
		showModalTimerRef.current = setInterval(
			checkUserActivity,
			FIVE_SECONDS,
		);

		return () => {
			clearInterval(showModalTimerRef.current);
		};
	}, [checkUserActivity]);

	const handleUserAction = () => {
		setUserActionDate();
	};

	useEffect(() => {
		const events = ["keydown", "mousedown"];
		events.forEach(event =>
			window.addEventListener(event, handleUserAction),
		);

		let scrollTimeout;
		const handleScroll = () => {
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(handleUserAction, 200);
		};

		window.addEventListener("scroll", handleScroll);

		return () => {
			events.forEach(event =>
				window.removeEventListener(event, handleUserAction),
			);
			window.removeEventListener("scroll", handleScroll);
			clearTimeout(scrollTimeout);
		};
	}, []);

	return (
		<div style={{ width: "100%" }}>
			<Header />
			<SideMenu />
			<Suspense fallback={<SpinnerOverlay isLazySpinner={true} />}>
				<main className="content">{body()}</main>
			</Suspense>
		</div>
	);
};

export const PrivateRoute = ({
	component: Component,
	requiredPermissions,
	pageName,
	errorTitle = undefined,
	isSubRoute,
	...rest
}) => {
	const { permissionLoading } = useContext(PermissionContext);

	usePendoInitialize(!!localStorage.getItem("user"));

	return (
		permissionLoading || (
			<Route
				{...rest}
				render={props => {
					if (!localStorage.getItem("user")) {
						return (
							<Redirect
								to={{
									pathname: URLS.login,
									state: { from: props.location },
								}}
							/>
						);
					}

					const hasPagePermissions =
						hasPermissions(requiredPermissions);

					if (hasPagePermissions) {
						if (isSubRoute) return <Component {...props} />;

						return (
							<PrivateLayout
								{...props}
								pageName={pageName}
								body={() => (
									<Component
										{...props}
										errorTitle={errorTitle}
									/>
								)}
							/>
						);
					}

					return isSubRoute ? (
						<PageNotFound />
					) : (
						<PrivateLayout body={() => <PageNotFound />} />
					);
				}}
			/>
		)
	);
};

export const AnonymousRoute = ({ component: Component, pageName, ...rest }) => {
	const urlData = new URLSearchParams(window.location.search);
	const authCode = urlData.has(AuthParams.authCode);
	const isRedirectToLogin = urlData.has(AuthParams.authLogin);
	const isTokenExpired = checkTokenExpiration();

	return (
		<Route
			{...rest}
			render={props => {
				if (!isTokenExpired && !authCode && !isRedirectToLogin) {
					return (
						<Redirect
							to={{
								pathname: "/",
								state: { from: props.location },
							}}
						/>
					);
				}
				setTitleAndFaviconForAurora(pageName);
				return <Component {...props} />;
			}}
		/>
	);
};

export const LogoutRoute = ({ ...rest }) => {
	return (
		<Route
			{...rest}
			render={props => {
				if (rest.location.pathname === URLS.logout) {
					if (localStorage.getItem("user")) {
						return <RedirectToLogout {...props} />;
					}
				}

				return (
					<Redirect
						to={{
							pathname: "/",
							state: { from: props.location },
						}}
					/>
				);
			}}
		/>
	);
};
